import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { debounce } from 'rxjs/operators';
import { App, Project, Site } from '../../models';
import { SitesDataService } from '../data-services';
import { GridColumnProperties } from './models';

import { BehaviorSubject, interval, Subject } from 'rxjs';
import { SiteColumnSettings } from '../../models/site-column-settings';
import { SitesService } from '../sites.service';
import { GridRendererFormattingData } from './models/siteGrid/grid-renderer-formatting-data';
import { GridRendererServiceModel } from './models/siteGrid/grid-renderer-logic-service-model';

@Injectable({
    providedIn: 'root',
})

export class GridRendererLogicService {
    private localSessionStorageKey = 'siteGridsFormattingMap';

    private _activeDragSourceGridId = new BehaviorSubject<string | null>(null);
    private _calculateGridWidth = new Subject<boolean>();

    activeDragSourceGridId$ = this._activeDragSourceGridId.asObservable();
    calculateGridWidth$ = this._calculateGridWidth.asObservable()
        .pipe(
            debounce(() => interval(250)),
        );

    model: GridRendererServiceModel = new GridRendererServiceModel();

    get activeDragSourceGridId(): string | null {
        return this._activeDragSourceGridId.value;
    }

    get currentSite(): Site {
        return this._sitesService.model.currentSite;
    }

    constructor(
        private _sitesService: SitesService,
        private _sitesDataService: SitesDataService,
        private _snackBar: MatSnackBar
    ) { }

    setSiteGridsFormattingMap(gridId: string): void {
        const currentGridFormattingData = this.currentSite.columnSettings?.find((x) => x.hasOwnProperty(gridId));
        if (!currentGridFormattingData) {
            this.loadModelFromSesion(this.currentSite.id);

            if (!this.model.siteGridsFormattingMap.has(this.currentSite.id)) {
                this.model.siteGridsFormattingMap.set(this.currentSite.id, new Map<string, GridRendererFormattingData>());
            }

            if (!this.model.siteGridsFormattingMap.get(this.currentSite.id).has(gridId)) {
                this.model.siteGridsFormattingMap.get(this.currentSite.id).set(gridId, new GridRendererFormattingData());
            }

            return;
        }

        const gridRendererFormattingData = new GridRendererFormattingData();
        gridRendererFormattingData.columnSettings = currentGridFormattingData[gridId];

        if (this.model.siteGridsFormattingMap.has(this.currentSite.id)) {
            this.model.siteGridsFormattingMap.get(this.currentSite.id).set(gridId, gridRendererFormattingData);
        }
        else {
            this.model.siteGridsFormattingMap.set(this.currentSite.id, new Map<string, GridRendererFormattingData>());
            this.model.siteGridsFormattingMap.get(this.currentSite.id).set(gridId, gridRendererFormattingData);
        }

        this.updateReportFormattingDataInSession(this.currentSite, gridId);
    }

    loadModelFromSesion(siteId: string): void {
        if (sessionStorage.getItem(this.localSessionStorageKey) == null) {
            return;
        }

        const mapInSession = (JSON.parse(sessionStorage.getItem(this.localSessionStorageKey)));
        const map = new Map<string, Map<string, GridRendererFormattingData>>();
        for (const key in mapInSession) {
            const innerMap = new Map();
            for (const innerKey in mapInSession[key]) {
                innerMap.set(innerKey, mapInSession[key][innerKey]);
            }
            map.set(key, innerMap);
        }

        if (!map.has(siteId) && !map.get(siteId)) {
            return;
        }

        this.model.siteGridsFormattingMap.set(siteId, map.get(siteId));
    }

    updateReportFormattingDataInSession(currentSite: Site, siteGridId: string): void {
        const currentGridFormattingData = this.model.siteGridsFormattingMap.get(currentSite.id)?.get(siteGridId);
        if (currentGridFormattingData && (currentGridFormattingData.columnSettings.length || currentGridFormattingData.filters.length || currentGridFormattingData.numLockedColumns > 0)) {
            this.model.siteGridsFormattingMap.get(currentSite.id)?.set(siteGridId, currentGridFormattingData);
            sessionStorage.setItem(this.localSessionStorageKey, JSON.stringify(this.convertToObject()));
        }
    }

    convertToObject(): any {
        const result: Map<string, Map<string, GridRendererFormattingData>> = new Map<string, Map<string, GridRendererFormattingData>>();

        this.model.siteGridsFormattingMap.forEach((innerMap, siteId) => {
            const siteObject: Map<string, GridRendererFormattingData> = new Map<string, GridRendererFormattingData>();
            innerMap.forEach((value, secondId) => {
                siteObject[secondId] = value;
            });
            result[siteId] = siteObject;
        });

        return result;
    }

    updateGridColumnWidth(currentApp: App, currentProject: Project, currentSite: Site, gridId: string, columnChanged: GridColumnProperties): void {
        this._sitesDataService.updateSiteGridColumnWidth(currentApp, currentProject, currentSite, gridId, columnChanged)
            .subscribe({
                next: (result: SiteColumnSettings[]) => {
                    this.currentSite.columnSettings = result;
                },
                error: () => {
                    this._snackBar.open('Error updating site column width', 'Close', { duration: 2000 });
                }
            });
    }

    updateGridColumnAggregator(currentApp: App, currentProject: Project, currentSite: Site, gridId: string, columnChanged: GridColumnProperties): void {
        this._sitesDataService.updateSiteGridColumnAggregator(currentApp, currentProject, currentSite, gridId, columnChanged)
            .subscribe({
                next: (result: SiteColumnSettings[]) => {
                    this.currentSite.columnSettings = result;
                },
                error: () => {
                    this._snackBar.open('Error updating site column aggregation', 'Close', { duration: 2000 });
                }
            });
    }

    calculateGridWidth(): void {
        this._calculateGridWidth.next(true);
    }

    setActiveDragSourceGridId(gridId: string | null): void {
        this._activeDragSourceGridId.next(gridId);
    }

    reorderRows(data: any[], draggedItem: any, targetItem: any, dropPosition: string): any[] | null {
        if (!draggedItem || !targetItem || draggedItem.id === targetItem.id) {
            return null;
        }

        if (draggedItem.parentId !== targetItem.parentId) {
            return null;
        }

        if (draggedItem.parentId) {
            return this.reorderChildRows(data, draggedItem, targetItem, dropPosition);
        }
        else {
            return this.reorderParentRows(data, draggedItem, targetItem, dropPosition);
        }
    }

    private reorderChildRows(data: any[], draggedItem: any, targetItem: any, dropPosition: string): any[] {
        const siblings = data.filter(row => row.parentId === draggedItem.parentId);

        const reorderedSiblings = siblings.filter(row => row.id !== draggedItem.id);
        const targetIndex = reorderedSiblings.findIndex(row => row.id === targetItem.id);
        const insertIndex = dropPosition === 'before' ? targetIndex : targetIndex + 1;
        reorderedSiblings.splice(insertIndex, 0, draggedItem);

        const parentIndex = data.findIndex(row => row.id === draggedItem.parentId);
        const parentRow = data[parentIndex];
        const beforeParent = data.filter((row, i) => i < parentIndex && row.parentId !== draggedItem.parentId);
        const afterParent = data.filter((row, i) => i > parentIndex && row.parentId !== draggedItem.parentId && row.id !== draggedItem.parentId);

        return [...beforeParent, parentRow, ...reorderedSiblings, ...afterParent];
    }

    private reorderParentRows(data: any[], draggedItem: any, targetItem: any, dropPosition: string): any[] {
        const draggedChildren = data.filter(row => row.parentId === draggedItem.id);
        const draggedBlock = [draggedItem, ...draggedChildren];
        const draggedIds = new Set(draggedBlock.map(row => row.id));

        const remainingData = data.filter(row => !draggedIds.has(row.id));

        const targetIndex = remainingData.findIndex(row => row.id === targetItem.id);
        let insertIndex: number;

        if (dropPosition === 'before') {
            insertIndex = targetIndex;
        }
        else {
            let lastIndex = targetIndex;
            for (let i = targetIndex + 1; i < remainingData.length; i++) {
                if (remainingData[i].parentId === targetItem.id) {
                    lastIndex = i;
                }
                else {
                    break;
                }
            }
            insertIndex = lastIndex + 1;
        }

        return [
            ...remainingData.slice(0, insertIndex),
            ...draggedBlock,
            ...remainingData.slice(insertIndex)
        ];
    }
}
